import { useEffect, useState, useRef, useCallback } from 'react';
import type { FormEvent } from 'react';
import { Socket } from 'socket.io-client';

// Interface para as mensagens
interface Message {
  from: string;
  content: string;
  timestamp: number;
}

// Interface para os usuários online
interface OnlineUser {
  id: string;
  name: string;
}

// Props para receber o socket do componente pai
interface ChatProps {
  socket: Socket | null;
}

export default function ChatSocketIO({ socket }: ChatProps) {
  // Estados para gerenciar o chat
  const [messages, setMessages] = useState<Message[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [messageInput, setMessageInput] = useState<string>('');
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>(
    socket?.connected ? 'connected' : 'connecting'
  );
  const [typingUsers, setTypingUsers] = useState<string[]>([]);

  // Referência para o container de mensagens
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Ref para controlar se já nos juntamos ao chat
  const joinedChatRef = useRef(false);

  // Configurar listeners do Socket.io
  useEffect(() => {
    console.log('ChatSocketIO: socket changed', socket);
    if (!socket) {
      console.log('ChatSocketIO: No socket provided');
      return;
    }

    // Atualizar status de conexão
    setConnectionStatus(socket.connected ? 'connected' : 'connecting');

    // Listener para mensagens de chat
    const handleChatMessage = (message: Message) => {
      console.log('Received chat message:', message);
      setMessages(prev => [...prev, message]);
    };

    // Listener para lista de usuários
    const handleUserList = (users: OnlineUser[]) => {
      console.log('Received user list:', users);
      setOnlineUsers(users);
    };

    // Listener para novos usuários
    const handleUserJoined = (user: OnlineUser) => {
      console.log('User joined:', user);
      setOnlineUsers(prev => {
        // Verificar se o usuário já existe
        if (prev.some(u => u.id === user.id)) {
          return prev;
        }
        return [...prev, user];
      });

      // Adicionar mensagem de sistema
      setMessages(prev => [...prev, {
        from: 'system',
        content: `${user.name} entrou no chat`,
        timestamp: Date.now()
      }]);
    };

    // Listener para usuários que saíram
    const handleUserLeft = (userId: string) => {
      console.log('User left:', userId);

      // Encontrar o nome do usuário antes de removê-lo
      const user = onlineUsers.find(u => u.id === userId);
      const userName = user ? user.name : `User-${userId.substring(0, 4)}`;

      setOnlineUsers(prev => prev.filter(u => u.id !== userId));

      // Adicionar mensagem de sistema
      setMessages(prev => [...prev, {
        from: 'system',
        content: `${userName} saiu do chat`,
        timestamp: Date.now()
      }]);

      // Remover dos usuários digitando
      setTypingUsers(prev => prev.filter(id => id !== userId));
    };

    // Listener para usuários digitando
    const handleUserTyping = (data: { userId: string, isTyping: boolean }) => {
      if (data.isTyping) {
        setTypingUsers(prev => {
          if (prev.includes(data.userId)) return prev;
          return [...prev, data.userId];
        });
      } else {
        setTypingUsers(prev => prev.filter(id => id !== data.userId));
      }
    };

    // Função para juntar-se ao chat (apenas uma vez)
    const joinChat = () => {
      if (joinedChatRef.current) {
        console.log('Already joined chat, skipping');
        return;
      }

      console.log('Joining chat...');
      socket.emit('chat:join', {});
      joinedChatRef.current = true;
    };

    // Listener para conexão estabelecida
    const handleConnect = () => {
      console.log('Socket.io connected');
      setConnectionStatus('connected');
      // Notificar o servidor que entramos no chat
      joinChat();
    };

    // Listener para desconexão
    const handleDisconnect = () => {
      console.log('Socket.io disconnected');
      setConnectionStatus('disconnected');
      // Resetar o estado de join para que possamos nos juntar novamente após reconectar
      joinedChatRef.current = false;
    };

    // Registrar os listeners
    socket.on('chat:message', handleChatMessage);
    socket.on('chat:user_list', handleUserList);
    socket.on('chat:user_joined', handleUserJoined);
    socket.on('chat:user_left', handleUserLeft);
    socket.on('chat:typing', handleUserTyping);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // Se já estiver conectado, juntar-se ao chat (apenas uma vez)
    if (socket.connected) {
      console.log('Socket already connected, joining chat');
      // Pequeno atraso para garantir que tudo esteja pronto
      setTimeout(joinChat, 100);
    }

    // Cleanup
    return () => {
      socket.off('chat:message', handleChatMessage);
      socket.off('chat:user_list', handleUserList);
      socket.off('chat:user_joined', handleUserJoined);
      socket.off('chat:user_left', handleUserLeft);
      socket.off('chat:typing', handleUserTyping);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket, onlineUsers]);

  // Função para rolar para a última mensagem
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  // Efeito para rolar para baixo quando novas mensagens chegam
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Função para enviar mensagem
  const sendMessage = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!messageInput.trim() || !socket) return;

    // Enviar mensagem via Socket.io
    socket.emit('chat:message', {
      content: messageInput
    });

    // Adicionar a mensagem à lista local
    setMessages(prev => [...prev, {
      from: socket.id,
      content: messageInput,
      timestamp: Date.now()
    }]);

    // Limpar o input
    setMessageInput('');

    // Notificar que parou de digitar
    socket.emit('chat:typing', { isTyping: false });
  };

  // Função para lidar com a digitação
  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessageInput(e.target.value);

    if (!socket) return;

    // Notificar que está digitando
    if (e.target.value.trim()) {
      socket.emit('chat:typing', { isTyping: true });
    } else {
      socket.emit('chat:typing', { isTyping: false });
    }
  };

  // Função para obter o nome de exibição de um usuário
  const getDisplayName = (userId: string): string => {
    if (userId === 'system') return 'Sistema';
    if (userId === socket?.id) return 'Você';

    const user = onlineUsers.find(u => u.id === userId);
    return user ? user.name : `User-${userId.substring(0, 4)}`;
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: '#000',
      color: '#fff'
    }}
    // Previne que eventos do chat afetem a cena 3D
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => e.stopPropagation()}
    onKeyUp={(e) => e.stopPropagation()}
    onKeyPress={(e) => e.stopPropagation()}
    onMouseDown={(e) => e.stopPropagation()}
    onMouseUp={(e) => e.stopPropagation()}
    onMouseMove={(e) => e.stopPropagation()}
    onWheel={(e) => e.stopPropagation()}>
      <div style={{
        padding: '12px',
        borderBottom: '1px solid rgba(138, 43, 226, 0.3)',
        backgroundColor: 'rgba(138, 43, 226, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h3 style={{ margin: 0, fontSize: '14px' }}>
          Multiverso Chat
        </h3>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: connectionStatus === 'connected'
              ? '#4CAF50'
              : connectionStatus === 'connecting'
                ? '#FFC107'
                : '#F44336'
          }} />
          {connectionStatus === 'connected'
            ? 'Conectado'
            : connectionStatus === 'connecting'
              ? 'Conectando...'
              : 'Desconectado'}
          {connectionStatus !== 'connected' && socket && (
            <button
              onClick={() => {
                console.log('Reconexão manual do Socket.io iniciada...');
                socket.connect();
              }}
              style={{
                marginLeft: '8px',
                padding: '2px 6px',
                fontSize: '10px',
                backgroundColor: '#8a2be2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Reconectar
            </button>
          )}
        </div>
      </div>

      {/* Removido o bloco de informações detalhadas de usuários conectados */}

      <div
        ref={messagesContainerRef}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: '12px',
          overflowY: 'auto',
          scrollBehavior: 'smooth',
          msOverflowStyle: 'none',
          scrollbarWidth: 'thin',
        }}
      >
        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              alignSelf: msg.from === 'system'
                ? 'center'
                : msg.from === socket?.id
                  ? 'flex-end'
                  : 'flex-start',
              maxWidth: msg.from === 'system' ? '90%' : '80%',
              backgroundColor: msg.from === 'system'
                ? 'rgba(100, 100, 100, 0.2)'
                : msg.from === socket?.id
                  ? 'rgba(138, 43, 226, 0.3)'
                  : 'rgba(255, 255, 255, 0.1)',
              padding: '8px 12px',
              borderRadius: '12px',
              fontSize: msg.from === 'system' ? '12px' : '14px',
              fontStyle: msg.from === 'system' ? 'italic' : 'normal',
            }}
          >
            {msg.from !== 'system' && (
              <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '4px' }}>
                {getDisplayName(msg.from)}
              </div>
            )}
            {msg.content}
          </div>
        ))}
      </div>

      {typingUsers.length > 0 && (
        <div style={{
          padding: '4px 12px',
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.6)',
          fontStyle: 'italic'
        }}>
          {typingUsers.length === 1
            ? `${getDisplayName(typingUsers[0])} está digitando...`
            : `${typingUsers.length} pessoas estão digitando...`}
        </div>
      )}

      <form
        onSubmit={sendMessage}
        style={{
          display: 'flex',
          gap: '8px',
          padding: '12px',
          borderTop: '1px solid rgba(138, 43, 226, 0.3)',
          backgroundColor: 'rgba(0, 0, 0, 0.3)'
        }}
        // Previne que o form afete a cena 3D
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
        onKeyPress={(e) => e.stopPropagation()}
      >
        <input
          type="text"
          value={messageInput}
          onChange={handleTyping}
          placeholder="Digite sua mensagem..."
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: '20px',
            border: '1px solid rgba(138, 43, 226, 0.3)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: '#fff',
            outline: 'none'
          }}
          // Previne que o input afete a cena 3D
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
          onKeyPress={(e) => e.stopPropagation()}
        />
        <button
          type="submit"
          disabled={!socket || connectionStatus !== 'connected'}
          style={{
            padding: '8px 16px',
            borderRadius: '20px',
            border: 'none',
            backgroundColor: connectionStatus === 'connected'
              ? 'rgba(138, 43, 226, 0.8)'
              : 'rgba(138, 43, 226, 0.3)',
            color: '#fff',
            cursor: connectionStatus === 'connected' ? 'pointer' : 'not-allowed'
          }}
        >
          Enviar
        </button>
      </form>
    </div>
  );
}
